import { Component, inject, output, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { PolicyIssueService, UnissuedMember } from '../../services/policy-issue';
import { ToastService } from '../../services/toast';
import { IndDatePipe } from '../../pipes/ind-date';

/**
 * "Paid but not issued" — the only place these members surface.
 *
 * A payment that succeeds normally stamps a policy number, the coverage dates and a certificate.
 * When the master policy's number series is exhausted (or its start/end range does not cover the
 * suffix in use) the payment still succeeds and the member is still marked PAID — but no policy is
 * minted and no certificate is ever sent. Nothing goes red; the customer simply waits.
 *
 * So this panel stays completely hidden while there is nothing wrong, and appears with a count and
 * a per-member reason the moment there is. "Issue policy" re-runs the same allocator the payment
 * path uses, so a repaired number follows exactly the same rules — and pressing it twice reports
 * the existing number instead of minting a second one.
 */
@Component({
  selector: 'app-unissued-panel',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, TooltipModule, IndDatePipe],
  template: `
    @if (svc.rows().length) {
      <div class="unissued" [class.open]="open()">
        <div class="head" (click)="open.set(!open())">
          <i class="pi pi-exclamation-triangle"></i>
          <div class="txt">
            <strong>
              {{ svc.rows().length }}
              {{ svc.rows().length === 1 ? 'member has' : 'members have' }} paid but hold no policy number
            </strong>
            <span>
              No certificate was produced for
              {{ svc.rows().length === 1 ? 'them' : 'them' }}.
              @if (svc.readyCount()) {
                {{ svc.readyCount() }} can be issued right now.
              } @else {
                None can be issued yet — see the reason against each.
              }
            </span>
          </div>
          <button type="button" class="toggle">
            {{ open() ? 'Hide' : 'Review' }}
            <i class="pi" [class.pi-chevron-down]="!open()" [class.pi-chevron-up]="open()"></i>
          </button>
        </div>

        @if (open()) {
          <div class="body">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Onboarded by</th>
                  <th>Paid</th>
                  <th>Why it is stuck</th>
                  <th class="ta-r">Action</th>
                </tr>
              </thead>
              <tbody>
                @for (r of svc.rows(); track r.id) {
                  <tr>
                    <td>
                      <span class="nm">{{ r.fullName || ('Member #' + r.id) }}</span>
                      <small class="mono">{{ r.mobileNumber || '—' }}</small>
                    </td>
                    <td>{{ r.partnerName || r.clientName || '—' }}</td>
                    <td class="nowrap">{{ r.paidAt ? (r.paidAt | indDate) : '—' }}</td>
                    <td>
                      <p-tag [value]="r.reason || '—'" [severity]="svc.isReady(r) ? 'info' : 'warn'" />
                      @if (svc.isReady(r) && r.seriesSuffix) {
                        <!-- The series is what almost always ran out, so show it without a drill-in. -->
                        <small class="mono">
                          {{ r.masterPolicyNumber || 'policy' }} · {{ r.seriesSuffix }}
                          {{ r.seriesStartNo }}–{{ r.seriesEndNo }}
                        </small>
                      }
                    </td>
                    <td class="ta-r">
                      <p-button
                        [label]="svc.issuing() === r.id ? 'Issuing…' : 'Issue policy'"
                        [icon]="svc.issuing() === r.id ? 'pi pi-spin pi-spinner' : 'pi pi-verified'"
                        size="small"
                        [disabled]="!svc.isReady(r) || svc.issuing() !== null"
                        [pTooltip]="tipFor(r)"
                        tooltipPosition="left"
                        (onClick)="issue(r)" />
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .unissued {
      border: 1px solid #fcd34d;
      background: #fffbeb;
      border-radius: 12px;
      margin-bottom: 16px;
      overflow: hidden;
    }
    .head {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      cursor: pointer;
    }
    .head > i { color: #b45309; font-size: 18px; flex: none; }
    .txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
    .txt strong { font-size: 13.5px; color: #78350f; }
    .txt span { font-size: 12.5px; color: #92400e; }

    .toggle {
      display: inline-flex; align-items: center; gap: 6px;
      border: 1px solid #fcd34d; background: #fff; color: #92400e;
      font: inherit; font-size: 12.5px; font-weight: 600;
      padding: 6px 12px; border-radius: 8px; cursor: pointer; white-space: nowrap;
    }
    .toggle:hover { background: #fef3c7; }

    .body { border-top: 1px solid #fcd34d; background: #fff; overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
    th, td { padding: 9px 14px; text-align: left; vertical-align: top; }
    th { font-weight: 600; color: var(--muted, #6b7280); background: #fafafa; white-space: nowrap; }
    tbody tr + tr td { border-top: 1px solid var(--line, #eef2f5); }
    .ta-r { text-align: right; }
    .nowrap { white-space: nowrap; }
    .nm { display: block; font-weight: 600; }
    .mono { display: block; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; color: var(--muted, #6b7280); }

    @media (prefers-color-scheme: dark) {
      .unissued { background: #2a1f07; border-color: #92400e; }
      .txt strong, .txt span { color: #fcd34d; }
      .toggle { background: transparent; color: #fcd34d; border-color: #92400e; }
      .body { background: transparent; border-top-color: #92400e; }
      th { background: transparent; }
    }
  `],
})
export class UnissuedPanel implements OnInit {
  readonly svc = inject(PolicyIssueService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  /** So the host can refresh its own list — the member now has a policy number to show. */
  readonly issued = output<number>();

  readonly open = signal(false);

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.svc.refresh();
  }

  tipFor(r: UnissuedMember): string {
    if ((r.kycStatus || '').toUpperCase() !== 'VERIFIED')
      return 'Verify the member by OTP first — issuance then runs automatically.';
    if (!r.masterPolicyId)
      return 'This member has no master policy, so there is no series to draw a number from.';
    return 'Mint the policy number and send the certificate.';
  }

  issue(r: UnissuedMember) {
    if (!this.svc.isReady(r) || this.svc.issuing() !== null) return;

    this.svc.issuing.set(r.id);
    this.svc.reissue(r.id).subscribe({
      next: (res) => {
        this.svc.issuing.set(null);
        if (res?.success === false) {
          this.toast.error(res?.message || 'The policy could not be issued.');
          return;
        }
        this.toast.success(res?.message || 'Policy issued.', 'Issued');
        this.svc.remove(r.id);
        this.issued.emit(r.id);
      },
      error: () => {
        this.svc.issuing.set(null);
        this.toast.error('The policy could not be issued. Please try again.');
      },
    });
  }
}
